import { RichTextDisplay, StatBar, VisibilityBadge } from '@/components/shared';
import { useTemplateContext } from '@/pages/templates/useTemplateContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@startupquickstart/frontend';
import { Clock, Eye, Star } from 'lucide-react';

export function TemplateOverviewTab() {
  const { template, isLoading } = useTemplateContext();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Template not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat trio — the template's headline attributes at a glance. */}
      <StatBar
        items={[
          {
            label: 'Visibility',
            value: <VisibilityBadge visibility={template.visibility} />,
            icon: Eye,
          },
          { label: 'Featured', value: template.isFeatured ? 'Featured' : 'Standard', icon: Star },
          {
            label: 'Estimated Duration',
            value: template.estimatedDuration || 'Not set',
            icon: Clock,
          },
        ]}
      />

      {/* Description */}
      {template.description && (
        <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
          <CardHeader className="px-6 py-4 !pb-4 border-b">
            <CardTitle>Description</CardTitle>
            <CardDescription>Template overview</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <RichTextDisplay content={template.description} />
          </CardContent>
        </Card>
      )}

      {/* Cover Image */}
      {template.coverImageUrl && (
        <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
          <CardHeader className="px-6 py-4 !pb-4 border-b">
            <CardTitle>Cover Image</CardTitle>
            <CardDescription>Template thumbnail</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <img
              src={template.coverImageUrl}
              alt={template.name}
              className="rounded-lg max-h-64 object-cover"
            />
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {template.tags && template.tags.length > 0 && (
        <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
          <CardHeader className="px-6 py-4 !pb-4 border-b">
            <CardTitle>Tags</CardTitle>
            <CardDescription>Template categories</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-2">
              {template.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
